import { useApiCall } from './useApiCall';
import { useCallback } from 'react';
import { IZeroStageValues } from '../components/pages/user/caseDraft/stages/common/zeroStage/ZeroStages.types';
import { IFirstStageValues } from '../components/pages/user/caseDraft/stages/common/firstStage/FirstStage.types';
import {
  IDraftCaseAcromegaly,
  IDraftCaseCushing,
  IDraftCaseCushingV2
} from '../components/pages/user/caseDraft/CaseDraft.types';
import { useProgressBarStore } from '../store/hooks';
import { ISecondStageValues } from '../components/pages/user/caseDraft/stages/acromegaly/secoundStage/SecondStage.types';
import { ISecondStageValues as ISecondStageValuesCushing } from '../components/pages/user/caseDraft/stages/cushing/secondStage/SecondStage.types';
import { IThirdStageValues } from '../components/pages/user/caseDraft/stages/acromegaly/thirdStage/ThirdStage.types';
import { IThirdStageValues as IThirdStageValuesCushing } from '../components/pages/user/caseDraft/stages/cushing/thirdStage/ThirdStage.types';

import { DiseaseType, ICaseStatus } from '../interfaces/common/common';
import {
  convertProgressBarAcromegaly,
  convertProgressBarCushing,
  convertProgressBarCushingV2
} from '../components/pages/user/caseDraft/CaseDraft.helper';
import { IFourthStageValues } from '../components/pages/user/caseDraft/stages/common/fourthStage/FourthStage.types';
import { IFirstStageValuesCushingV2 } from '../components/pages/user/caseDraft/stages/cushing-v2/firstStage/FirstStage.types';
import {
  ISecondStageValuesCushingV2,
  TFile
} from '../components/pages/user/caseDraft/stages/cushing-v2/secondStage/SecondStage.types';
import { IThirdStageValuesCushingV2 } from '../components/pages/user/caseDraft/stages/cushing-v2/thirdStage/ThirdStage.types';

export const useDraftCaseApi = () => {
  const { api } = useApiCall();
  const progressBarStore = useProgressBarStore();

  const getDraftCaseFirstStage = useCallback(
    async (id: number, type: DiseaseType, isDraft: boolean = false, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/${id}` : `medical-case/${type}/${id}`;
      const res = await api.apiInstance.get(url);

      if (res) {
        if (type === 'acromegaly') {
          const data: IDraftCaseAcromegaly = res.data;
          const convert = convertProgressBarAcromegaly(data);

          if (convert.status === ICaseStatus.DRAFT) {
            progressBarStore.addStages(
              convert.step,
              convert.zeroStage,
              convert.firstStage,
              convert.secondStage,
              convert.thirdStage,
              convert.fourthStage
            );
          }

          progressBarStore.setIsDraft(isDraft ? true : convert.status === ICaseStatus.DRAFT);
        } else if (version) {
          const data: IDraftCaseCushingV2 = res.data;
          const convert = convertProgressBarCushingV2(data);

          if (convert.status === ICaseStatus.DRAFT) {
            progressBarStore.addStages(
              convert.step,
              convert.zeroStage,
              convert.firstStage,
              convert.secondStage,
              convert.thirdStage,
              convert.fourthStage
            );
          }
          progressBarStore.setIsDraft(isDraft ? true : convert.status === ICaseStatus.DRAFT);
        } else {
          const data: IDraftCaseCushing = res.data;
          const convert = convertProgressBarCushing(data);

          if (convert.status === ICaseStatus.DRAFT) {
            progressBarStore.addStages(
              convert.step,
              convert.zeroStage,
              convert.firstStage,
              convert.secondStage,
              convert.thirdStage,
              convert.fourthStage
            );
          }

          progressBarStore.setIsDraft(isDraft ? true : convert.status === ICaseStatus.DRAFT);
        }
      }
    },
    [api.apiInstance, progressBarStore]
  );

  const draftCaseZeroStage = useCallback(
    async (zeroStage: IZeroStageValues, type: DiseaseType, version?: string) => {
      const url = version ? `medical-case/${type}/${version}/first-step` : `medical-case/${type}/first-step`;
      const res = await api.apiInstance.post(url, zeroStage);

      if (res) {
        return res.data.id;
      }
    },
    [api.apiInstance]
  );

  const draftCaseZeroStageSaved = useCallback(
    async (zeroStage: IZeroStageValues, id: number, type: DiseaseType, version?: string) => {
      const url = version
        ? `medical-case/${type}/${version}/first-step/${id}`
        : `medical-case/${type}/first-step/${id}`;
      const res = await api.apiInstance.put(url, zeroStage);
      await getDraftCaseFirstStage(id, type, false, version);

      if (res) {
        return res.data.id;
      }
    },
    [api.apiInstance, getDraftCaseFirstStage]
  );

  const draftCaseFirstStage = useCallback(
    async (
      firstStage: IFirstStageValues | IFirstStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/second-step/${id}`
        : `medical-case/${type}/second-step/${id}`;
      const res = await api.apiInstance.put(url, firstStage);

      if (res) {
        return true;
      }
    },
    [api.apiInstance]
  );

  const draftCaseFirstStageSaved = useCallback(
    async (
      firstStage: IFirstStageValues | IFirstStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/second-step/draft/${id}`
        : `medical-case/${type}/second-step/draft/${id}`;

      const res = await api.apiInstance.put(url, firstStage);
      await getDraftCaseFirstStage(id, type, false, version);

      if (res) {
        return true;
      }
    },
    [api.apiInstance, getDraftCaseFirstStage]
  );

  const draftCaseSecondSage = useCallback(
    async (
      secondStage: ISecondStageValues | ISecondStageValuesCushing | ISecondStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/third-step/${id}`
        : `medical-case/${type}/third-step/${id}`;

      const res = await api.apiInstance.put(url, secondStage);

      if (res) {
        return true;
      }
    },
    [api.apiInstance]
  );

  const draftCaseSecondStageFiles = useCallback(
    async (fileKey: string, id: number, file: File | null, callback: (res: TFile, fileKey: string) => void) => {
      const form = new FormData();
      form.append('file', file as File);
      const url = `medical-case/cushing/v2/third-step/${fileKey}/${id}`;

      const res = await api.apiInstance.post(url, form, true, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (res) {
        callback(res, fileKey);
      }
    },
    [api.apiInstance]
  );

  const draftCaseSecondStageFilesWithIndex = useCallback(
    async (
      fileKey: string,
      id: number,
      file: File | null,
      index: number,
      callback: (res: TFile, fileKey: string, index?: number) => void
    ) => {
      const form = new FormData();
      form.append('file', file as File);
      const url = `medical-case/cushing/v2/third-step/${fileKey}/${id}/${index}`;

      const res = await api.apiInstance.post(url, form, true, { headers: { 'Content-Type': 'multipart/form-data' } });

      if (res) {
        callback(res, `${fileKey}[${index}].otherMethodFiles`, index);
      }
    },
    [api.apiInstance]
  );

  const draftCaseSecondStageSaved = useCallback(
    async (
      secondStage: ISecondStageValues | ISecondStageValuesCushing | ISecondStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/third-step/draft/${id}`
        : `medical-case/${type}/third-step/draft/${id}`;
      const res = await api.apiInstance.put(url, secondStage);
      await getDraftCaseFirstStage(id, type, false, version);

      if (res) {
        return true;
      }
    },
    [api.apiInstance, getDraftCaseFirstStage]
  );

  const draftCaseThirdSage = useCallback(
    async (
      thirdStage: IThirdStageValues | IThirdStageValuesCushing | IThirdStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/fourth-step/${id}`
        : `medical-case/${type}/fourth-step/${id}`;

      const res = await api.apiInstance.put(url, thirdStage);

      if (res) {
        return true;
      }
    },
    [api.apiInstance]
  );

  const draftCaseThirdStageSaved = useCallback(
    async (
      thirdStage: IThirdStageValues | IThirdStageValuesCushing | IThirdStageValuesCushingV2,
      id: number,
      type: DiseaseType,
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/fourth-step/draft/${id}`
        : `medical-case/${type}/fourth-step/draft/${id}`;

      const res = await api.apiInstance.put(url, thirdStage);
      await getDraftCaseFirstStage(id, type, false, version);

      if (res) {
        return true;
      }
    },
    [api.apiInstance, getDraftCaseFirstStage]
  );

  const draftCaseFourthStageSaved = useCallback(
    async (
      thirdStage: IFourthStageValues,
      id: number,
      type: DiseaseType,
      status: 'DRAFT' | 'SENT',
      version?: string
    ) => {
      const url = version
        ? `medical-case/${type}/${version}/fifth-step/${id}`
        : `medical-case/${type}/fifth-step/${id}`;

      const res = await api.apiInstance.put(url, { ...thirdStage, status });
      await getDraftCaseFirstStage(id, type, true, version);

      if (res) {
        return true;
      }
    },
    [api.apiInstance, getDraftCaseFirstStage]
  );

  return {
    draftCaseFourthStageSaved,
    draftCaseThirdSage,
    draftCaseThirdStageSaved,
    draftCaseSecondSage,
    draftCaseSecondStageSaved,
    getDraftCaseFirstStage,
    draftCaseZeroStageSaved,
    draftCaseFirstStage,
    draftCaseFirstStageSaved,
    draftCaseZeroStage,
    draftCaseSecondStageFiles,
    draftCaseSecondStageFilesWithIndex
  };
};
